import React, { useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import "../../../css/allFacility.css";
import "../../../css/tournaments.css";
import { Default } from "../../layouts/default";
import LazyImage from "../../common/LazyImage";
import axios from "axios";
import Loader from "../../common/loader";
import bannerBv from "../../../image/banner-bv.png";
import { Context as AuthContext } from "../../../context/AuthContext";

const BASE_URL = process.env.REACT_APP_API_ENDPOINT;
const PENDING_TOURNAMENT_KEY = "bv_pending_tournament_register";

const tournamentMatchesFilter = (item, filterId) => {
  if (filterId === "all") return true;

  return String(item.sport_id) === String(filterId);
};

const Tournaments = () => {
  const navigate = useNavigate();
  const { state, openLoginModal } = useContext(AuthContext);
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [tournaments, setTournaments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sports, setSports] = useState([{ id: "all", label: "All" }]);

  const [error, setError] = useState(null);

  const isAuthed = !!Cookies.get("USER_TOKEN") || !!state.userData;

  const goToRegistration = (tournamentId) => {
    if (!tournamentId) return;

    if (!isAuthed) {
      sessionStorage.setItem(PENDING_TOURNAMENT_KEY, String(tournamentId));
      openLoginModal(true);
      return;
    }

    navigate(`/tournaments/${tournamentId}/register`);
  };

  const displayedTournaments = useMemo(() => {
    let list = tournaments.filter((item) =>
      tournamentMatchesFilter(item, activeFilter),
    );

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((item) => {
        const haystack = [item.title, item.location, item.sport, item.date]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    return list;
  }, [tournaments, activeFilter, searchQuery]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isAuthed) return;
    const pendingId = sessionStorage.getItem(PENDING_TOURNAMENT_KEY);
    if (!pendingId) return;
    sessionStorage.removeItem(PENDING_TOURNAMENT_KEY);
    navigate(`/tournaments/${pendingId}/register`);
  }, [isAuthed, navigate]);

  const getTournaments = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await axios.get(`${BASE_URL}/get-all-tournament`);

      const tournamentData = response.data.tournament || [];

      setTournaments(tournamentData);

      // Create dynamic sports from tournament response
      const uniqueSports = Array.from(
        new Map(
          tournamentData.map((item) => [
            item.sport_id,
            {
              id: item.sport_id,
              label: item.sport_name,
            },
          ]),
        ).values(),
      );

      setSports(uniqueSports);
    } catch (error) {
      console.error("Error getting tournaments:", error);
      setError("Unable to load tournaments.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getTournaments();
  }, []);

  const formatTournamentDate = (startDate, endDate) => {
    if (!startDate || !endDate) return "";

    const start = new Date(startDate);
    const end = new Date(endDate);

    const startDay = start.getDate();
    const startMonth = start.toLocaleString("en-US", {
      month: "short",
    });

    const endDay = end.getDate();
    const endMonth = end.toLocaleString("en-US", {
      month: "short",
    });
    const endYear = end.getFullYear();

    return `${startDay} ${startMonth} - ${endDay} ${endMonth}, ${endYear}`;
  };

  useEffect(() => {
    const els = document.querySelectorAll(
      ".trn-page .bv-reveal:not(.is-revealed)",
    );
    if (!("IntersectionObserver" in window) || els.length === 0) {
      els.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [displayedTournaments.length, activeFilter, searchQuery]);

  const toolbarStatus = useMemo(() => {
    const count = displayedTournaments.length;
    const q = searchQuery.trim();

    if (count === 0) {
      return {
        type: "empty",
        message: q
          ? "No tournaments match your search"
          : "No tournaments for this sport — try another filter",
      };
    }

    if (q) {
      return {
        type: "line",
        count,
        text: `matching “${q}”`,
      };
    }

    if (activeFilter !== "all") {
      const label =
        sports.find((c) => String(c.id) === String(activeFilter))?.label ||
        activeFilter;

      return {
        type: "line",
        count,
        text: `in ${label}`,
      };
    }

    return {
      type: "line",
      count,
      text: "open for registration",
    };
  }, [displayedTournaments.length, searchQuery, activeFilter]);

  return (
    <Default>
      <Helmet>
        <title>Sports Tournaments | Register &amp; Compete | Book Venue</title>
        <meta
          name="description"
          content="Discover cricket, football, tennis and badminton tournaments near you. Register for leagues, cups and championships on Book Venue."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://bookvenue.app/tournaments" />
      </Helmet>

      <div className="trn-page">
        <header className="trn-hero">
          <div className="bv-container bv-container--wide trn-hero__inner">
            <nav className="trn-breadcrumb bv-reveal" aria-label="Breadcrumb">
              <Link to="/" className="trn-breadcrumb__link">
                <i className="fa-solid fa-house" aria-hidden="true" />
                Home
              </Link>
              <span className="trn-breadcrumb__sep" aria-hidden="true">
                <i className="fa-solid fa-chevron-right" />
              </span>
              <span className="trn-breadcrumb__current" aria-current="page">
                Tournaments
              </span>
            </nav>

            <div className="trn-hero__body">
              <div className="trn-hero__content bv-reveal" data-delay="100">
                <span className="trn-hero__eyebrow">
                  <i className="fa-solid fa-trophy" aria-hidden="true" />
                  Compete &amp; win
                </span>
                <h1 className="trn-hero__title">
                  Find tournaments{" "}
                  <span className="gradientText">near you</span>
                </h1>
                <p className="trn-hero__lede">
                  Browse cricket cups, football leagues, tennis opens and more.
                  Register your team in minutes — or host your own event on Book
                  Venue.
                </p>
                <button type="button" className="trn-hero__cta">
                  Host tournament
                  <span className="trn-hero__cta-icon" aria-hidden="true">
                    <i className="fa-solid fa-arrow-right" />
                  </span>
                </button>
              </div>

              <div className="trn-hero__visual bv-reveal" data-delay="180">
                <LazyImage
                  className="trn-hero__img"
                  src={bannerBv}
                  alt="Athletes competing in a sports tournament"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </header>

        <section className="trn-listings bv-section--cream">
          <div className="bv-container bv-container--wide">
            <div className="trn-stats-bar bv-reveal">
              {sports.map((stat, index) => (
                <React.Fragment key={stat.label}>
                  <div className="trn-stats-bar__item">
                    <span className="trn-stats-bar__icon" aria-hidden="true">
                      <i className={`fa-solid ${stat.icon}`} />
                    </span>
                    <span className="trn-stats-bar__value">{stat.value}</span>
                    <span className="trn-stats-bar__label">{stat.label}</span>
                  </div>
                  {index < sports.length - 1 && (
                    <span
                      className="trn-stats-bar__divider"
                      aria-hidden="true"
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="trn-toolbar bv-reveal" data-delay="100">
              <div className="af-toolbar__search trn-toolbar__search">
                <i
                  className="fa-solid fa-magnifying-glass"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  className="af-toolbar__search-input"
                  placeholder="Search events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search tournaments"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="af-toolbar__search-clear"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                  >
                    <i className="fa-solid fa-xmark" aria-hidden="true" />
                  </button>
                )}
              </div>

              <div
                className="trn-toolbar__filters"
                role="tablist"
                aria-label="Filter by sport"
              >
                {sports.map((chip) => {
                  const isActive = activeFilter === chip.id;
                  return (
                    <button
                      key={chip.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`trn-toolbar__chip ${isActive ? "is-active" : ""}`}
                      onClick={() => setActiveFilter(chip.id)}
                    >
                      {chip.img ? (
                        <span className="trn-toolbar__chip-icon">
                          <LazyImage src={chip.img} alt="" />
                        </span>
                      ) : (
                        <span className="trn-toolbar__chip-icon">
                          <i
                            className="fa-solid fa-border-all"
                            aria-hidden="true"
                          />
                        </span>
                      )}
                      {chip.label}
                    </button>
                  );
                })}
              </div>

              <div className="trn-toolbar__status" aria-live="polite">
                {toolbarStatus.type === "empty" && (
                  <span className="trn-toolbar__status-line trn-toolbar__status-line--muted">
                    {toolbarStatus.message}
                  </span>
                )}
                {toolbarStatus.type === "line" && (
                  <span className="trn-toolbar__status-line">
                    <strong>{toolbarStatus.count}</strong>
                    {toolbarStatus.count === 1
                      ? " tournament "
                      : " tournaments "}
                    {toolbarStatus.text}
                  </span>
                )}
              </div>

              <button type="button" className="trn-toolbar__more">
                <i className="fa-solid fa-sliders" aria-hidden="true" />
                Filters
              </button>
            </div>

            {displayedTournaments.length > 0 ? (
              <div className="trn-feed">
                {displayedTournaments.map((item, index) => (
                  <article
                    key={item.id}
                    className={`trn-card bv-reveal ${
                      index % 2 !== 0 ? "trn-card--reverse" : ""
                    }`}
                    data-delay={String(Math.min(index * 100, 400))}
                  >
                    <div className="trn-card__media">
                      <LazyImage src={item.image} alt={item.sport_name} />
                      <div className="trn-card__badges">
                        <span className="trn-card__badge trn-card__badge--sport">
                          <LazyImage src={item.sportIcon} alt="" />
                          {item.sport_name}
                        </span>
                        {item.is_premium && (
                          <span className="trn-card__badge trn-card__badge--premium">
                            <i
                              className="fa-solid fa-crown"
                              aria-hidden="true"
                            />
                            Premium
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="trn-card__body">
                      <span className="trn-card__number" aria-hidden="true">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <h2 className="trn-card__title">{item.name}</h2>

                      <div className="trn-card__meta">
                        <p className="trn-card__meta-row">
                          <span
                            className="trn-card__meta-icon"
                            aria-hidden="true"
                          >
                            <i className="fa-regular fa-calendar" />
                          </span>
                          {formatTournamentDate(item.start_date, item.end_date)}
                        </p>
                        <p className="trn-card__meta-row">
                          <span
                            className="trn-card__meta-icon"
                            aria-hidden="true"
                          >
                            <i className="fa-solid fa-location-dot" />
                          </span>
                          {item.facility_name}
                        </p>
                      </div>

                      <div className="trn-card__stats">
                        <div>
                          <span className="trn-card__stat-label">
                            Entry fee
                          </span>
                          <span className="trn-card__stat-value">
                            {item.entry_fee}
                          </span>
                        </div>
                        <div>
                          <span className="trn-card__stat-label">
                            Prize pool
                          </span>
                          <span className="trn-card__stat-value trn-card__stat-value--prize">
                            {item.prize_details}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="trn-card__cta"
                        onClick={() => goToRegistration(item.id)}
                      >
                        Register now
                        <span className="trn-card__cta-icon" aria-hidden="true">
                          <i className="fa-solid fa-arrow-right" />
                        </span>
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="af-empty bv-reveal trn-empty">
                <div className="af-empty__icon" aria-hidden="true">
                  <i className="fa-solid fa-trophy" />
                </div>
                <h3>No tournaments found</h3>
                <p>
                  {searchQuery.trim()
                    ? "Try a different search term or clear filters to see all events."
                    : "Try another sport filter or browse all tournaments."}
                </p>
                <button
                  type="button"
                  className="bv-btn bv-btn--primary"
                  onClick={() => {
                    setActiveFilter("all");
                    setSearchQuery("");
                  }}
                >
                  View all tournaments
                </button>
              </div>
            )}
          </div>
        </section>
      </div>
    </Default>
  );
};

export default Tournaments;
